<script>
import { defineComponent, ref, computed, watch, getCurrentInstance } from 'vue'
import { ValidationObserver } from 'vee-validate'
import { useSaveSectionOnScroll } from '../useSaveSectionOnScroll'
import FormItem from '@/components/Input/FormItem.vue'
import Checkbox from '@/components/Input/Checkbox.vue'
import FormInputGroup from '@/components/Input/FormInputGroup.vue'
import UnitSelector from '../TypeAndPrice/UnitSelector.vue'
import { useUnits } from '../useUnits'
import isEqual from 'lodash/isEqual'

export default defineComponent({
  components: {
    ValidationObserver,
    FormItem,
    Checkbox,
    FormInputGroup,
    UnitSelector,
},
  props: {
    value: {
      type: Array,
      required: true,
    },
    product: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      required: true,
    },
    permission: {
      type: Array,
      required: true,
    },
    itemCod: {
      type: Number,
      required: true,
    },
  },
  setup(props, { emit }) {
    const { $confirm, $t } = getCurrentInstance().proxy.$root

    const { el, dirty, validationObserver } = useSaveSectionOnScroll(emit)

    /** @type {import('@vue/composition-api').Ref<import('@/types/product').ProductFormModel>} */
    const model = ref(props.value)
    const edit = ref(false)
    const visible = ref(false)
    const flag = ref(false)
    const tempData = ref({})
    const ongkir = ref(0)
    const isItemCod = ref(props.itemCod)
    watch(() => props.value, (value) => {
      if (!isEqual(model.value, value)) model.value = value
    }, { deep: true })
    watch(() => model.value, (units) => emit('input', units) , { deep: true } )
    watch(() => props.itemCod, (value) => isItemCod.value = value)
    // watch(() => model.value[0]?.unit, (value) => {
    //   props.product.catalogs.items.forEach(item => {
    //     if (value && item.price) {
    //       item.price.uom = value
    //     }
    //   })
    // })

    const unitOnCatalogs = computed(() => [ ...new Set(props.product?.catalogs.items.map((item) => item.price.uom)) ])
    // watch(unitOnCatalogs, () => {
    //   unitOnCatalogs.value.forEach(unit => {
    //     addUnit(unit)
    //   })
    // })
    watch(() => unitOnCatalogs.value[0], (uom, oldValue) => {
      if (uom === oldValue) return

      const baseUom = model.value[0].unit

      if (model.value.length > 1 && baseUom !== uom) {
        $confirm({
          title: $t('product.change_unit'),
          content: $t('product.confirm_change_base_unit', { oldUnit: oldValue, newUnit: uom }),
          okText: $t('product.change_unit'),
          onOk: () => {
            model.value = [
              {
                ...model.value[0],
                unit: uom,
              },
            ]
           },
          onCancel: () => {
            emit('cancelBaseUomChange', oldValue)
          },
        })
      } else {
        model.value[0].unit = uom
      }
    }, { flush: 'pre' })

    const { units } = useUnits()
    const selectedUnit = computed(() => [ ...new Set(model.value.map(u => u.unit)) ])
    const addUnit = (unit) => {
      let unitValue = unit

      if (!unitValue) {
        unitValue = units.value.find(unit => !selectedUnit.value.includes(unit))
      }

      if (model.value.find(item => item.unit === unit)) {
        return
      }

      model.value.push({
        collapseOpen: true,
        unit: unitValue,
        denumerator: 1,
        numerator: 1,
        package: {
          height_unit: 'cm',
          width_unit: 'cm',
          length_unit: 'cm',
          height: null,
          width: null,
          length: null,
          weight: null,
          weight_unit: 'GR',
        },
      })
    }
    const removeUnit = (index) => {
      model.value.splice(index, 1)
      dirty.value = true
    }

    const handleEdit = () => {
      edit.value = !edit.value
      emit('changeEditable', {shipping: edit.value})
      if (edit.value === false) {
        const firstErrorElement = document.querySelector('#shipping')
        // submit.value = false
        window.scrollTo({
          behavior: 'smooth',
          top: firstErrorElement.getBoundingClientRect().top + window.pageYOffset - 225,
        })
      }
    }

    const ellipsisText = (text) => {
      if(!text) return '-'
      return text.length > 18 ? `${text.substring(0, 18)}...` : text
    }

    const toCurrency = (value) => {
      return `${Intl.NumberFormat('en-US').format(value || 0)}`
    }

    const handleEditOngkir = (row, vis) => {
      visible.value = vis
      tempData.value = row
      flag.value = row.is_free
      ongkir.value = row.estimated_shipping_fee
    }

    return {
      el,
      dirty,
      validationObserver,
      model,
      addUnit,
      removeUnit,
      selectedUnit,
      PACKAGE_SIZES: [
        { value: 'mm', label: 'MM' },
        { value: 'cm', label: 'CM' },
        { value: 'm', label: 'M' },
      ],
      edit,
      handleEdit,
      ellipsisText,
      toCurrency,
      visible,
      handleEditOngkir,
      tempData,
      flag,
      ongkir,
      isItemCod,
    }
  },
})
</script>

<template>
  <a-card ref="el">
    <a-skeleton v-if="loading" active />
    <ValidationObserver v-else ref="validationObserver">
      <div class="d-flex">
        <div>
          <h4 id="shipping" class="">
            {{ $t('product.weight_product_shipping') }}
          </h4>
        </div>
        <div v-if="$route.query.edit && !edit && permission.includes('WRITE')" class="ml-auto">
          <a-button
            type="primary"
            size="large"
            :ghost="edit ? false : true"
            @click="handleEdit"
          >
            {{ $t('utils.edit') }}
          </a-button>
        </div>
      </div>

      <div v-if="$route.query.edit">
        <a-col class="mb-3" :span="6">
          <div class="title-detail d-flex justify-content-start mb-2">
            {{ $t('product.physical_product') }}
          </div>
        </a-col>
        <a-col class="mb-4" :span="18">
          <div class="text-left mb-2">
            : {{ product.detail.physical ? $t('utils.yes') : $t('utils.no') }}
          </div>
        </a-col>
        <div v-if="product.detail.physical">
          <a-col class="mb-2" :span="24">
            <div class="font-weight-semibold d-flex justify-content-start mb-2">
              {{ $t('product.base_unit') }}
            </div>
          </a-col>
          <a-col class="mb-2" :span="6">
            <div class="title-detail d-flex justify-content-start mb-2">
              {{ $t('product.product_unit') }}
            </div>
          </a-col>
          <a-col class="mb-2" :span="18">
            <div class="text-left mb-2">
              : {{ model?.[0]?.unit }}
            </div>
          </a-col>
          <a-col class="mb-2" :span="6">
            <div class="title-detail d-flex justify-content-start mb-2">
              {{ $t('product.weight_unit') }}
            </div>
          </a-col>
          <a-col class="mb-2" :span="18">
            <div class="text-left mb-2">
              : {{ model?.[0]?.package?.weight_unit }}
            </div>
          </a-col>
          <a-col class="mb-4" :span="6">
            <div class="title-detail d-flex justify-content-start mb-2">
              {{ $t('product.weight_value') }}
            </div>
          </a-col>
          <a-col class="mb-4" :span="18">
            <div class="text-left mb-2">
              : {{ model?.[0]?.package?.weight }}
            </div>
          </a-col>
          <a-col class="mb-2" :span="24">
            <div class="font-weight-semibold d-flex justify-content-start mb-2">
              {{ $t('product.package_size') }}
            </div>
          </a-col>
          <a-col class="mb-2" :span="6">
            <div class="title-detail d-flex justify-content-start mb-2">
              {{ $t('product.width_value') }}
            </div>
          </a-col>
          <a-col class="mb-2" :span="18">
            <div class="text-left mb-2">
              : {{ model?.[0]?.package.width }} {{ model?.[0]?.package.width_unit }}
            </div>
          </a-col>
          <a-col class="mb-2" :span="6">
            <div class="title-detail d-flex justify-content-start mb-2">
              {{ $t('product.length_value') }}
            </div>
          </a-col>
          <a-col class="mb-2" :span="18">
            <div class="text-left mb-2">
              : {{ model?.[0]?.package.length }} {{ model?.[0]?.package?.length_unit }}
            </div>
          </a-col>
          <a-col class="mb-4" :span="6">
            <div class="title-detail d-flex justify-content-start mb-2">
              {{ $t('product.height_value') }}
            </div>
          </a-col>
          <a-col class="mb-4" :span="18">
            <div class="text-left mb-2">
              : {{ model?.[0]?.package.height }} {{ model?.[0]?.package.height_unit }}
            </div>
          </a-col>
        </div>
      </div>

      <FormItem>
        <Checkbox
          v-if="!$route.query.edit"
          v-model="product.detail.physical"
          :check-value="false"
          :uncheck-value="true"
          :label="$t('product.non_physical_product')"
          @input="(val) => $emit('input:physical', val)"
        />
      </FormItem>

      <div v-if="product.detail.physical" class="unit-wrapper">
        <div v-for="(unit, index) in model" :key="index" class="unit-item">
          <div
            v-if="index !== 0 && !$route.query.edit"
            class="unit-item__header cpoin"
            :class="{ 'unit-item__header--active': unit.collapseOpen }"
            @click.stop="unit.collapseOpen = !unit.collapseOpen"
          >
            <div class="unit-item__header__title">
              {{ unit.unit }}
            </div>
            <div class="actions ml-auto">
              <a-popconfirm :title="$t('product.confirm_remove_unit', { unit: unit.unit || '' })" @confirm="removeUnit(index)">
                <a-button
                  type="primary"
                  size="small"
                  ghost
                  icon="delete"
                  @click.stop=""
                >
                  {{ $t('utils.delete') }}
                </a-button>
              </a-popconfirm>
              <!-- @click="unit.collapseOpen = !unit.collapseOpen" -->
              <a-button class="toggle" ghost>
                <a-icon type="caret-right" />
              </a-button>
            </div>
          </div>
          <div v-if="(unit.collapseOpen || index === 0 ) && !$route.query.edit" class="unit-item__body">
            <a-row type="flex" :gutter="16" class="mb-2">
              <a-col v-if="index === 0" :sm="{ span: 12 }" :md="{ span: 8 }" :lg="{ span: 8 }">
                <FormItem :label="$t('product.base_unit')" :name="`${$t('product.base_unit')} [${unit.unit}]`" rules="required" immediate>
                  <UnitSelector v-model="model[0].unit" disabled class="h-48px" />
                </FormItem>
              </a-col>

              <template v-else>
                <a-col :sm="{ span: 12 }" :md="{ span: 7 }" :lg="{ span: 6 }">
                  <FormInputGroup
                    tag="div"
                    rules="required|numeric"
                    :label="$t('product.base_unit')"
                    :name="`${$t('product.base_unit')} [${unit.unit}]`"
                    label-class="ant-form-item-label mb-1px!"
                    class="h-48px mb-3"
                  >
                    <a-input-number
                      v-model.number="unit.numerator"
                      :min="1"
                      class="h-48px w-100 required"
                    />

                    <template #append>
                      <div class="ant-input-group-addon">
                        {{ model[0].unit }}
                      </div>
                    </template>
                  </FormInputGroup>
                </a-col>
                <a-col class="align-self-center">
                  <div class="pt-3 mt-1">
                    =
                  </div>
                </a-col>

                <a-col :sm="{ span: 12 }" :md="{ span: 8 }" :lg="{ span: 12 }" :xl="{ span: 8 }">
                  <FormInputGroup
                    tag="div"
                    rules="required|numeric"
                    :label="$t('product.quantity_conversion')"
                    :name="`${$t('product.quantity_conversion')} [${unit.unit}]`"
                    label-class="ant-form-item-label mb-1px!"
                    :class="{
                      'has-feedback has-error': !unit.unit,
                    }"
                    class="h-48px mb-3"
                  >
                    <!-- v-model.number="unit.denumerator" -->
                    <a-input-number
                      :value="unit.denumerator"
                      disabled
                      :min="1"
                      class="h-48px w-100 required bg-white"
                    />

                    <template #append>
                      <FormItem
                        class="ant-input-group-addon px-1"
                        style="margin: -2px -11px; width: 120px"
                        rules="required"
                        :name="`${$t('product.quantity_conversion')} unit [${unit.unit}]`"
                        immediate
                      >
                        <UnitSelector
                          v-model="unit.unit"
                          :excludes="selectedUnit"
                          :available-unit="model"
                          class="d-flex align-items-center"
                        />
                      </FormItem>
                    </template>
                  </FormInputGroup>
                </a-col>
              </template>

              <a-col :sm="{ span: 12 }" :md="{ span: 16 }" :lg="{ span: 16 }" :xl="{ span: 7, offset: index !== 0 ? 1 : 0 }">
                <!-- rules="required|numeric" -->
                <FormInputGroup
                  tag="div"
                  :rules="`required|max_value:${unit.package.weight_unit === 'kg' ? '50' : '50000'}`"
                  :name="`${$t('product.weight')} [${unit.unit}]`"
                  :label="$t('product.product_weight_per_unit', { unit: unit.unit || '-' })"
                  label-class="ant-form-item-label mb-1px!"
                  class="h-48px mb-3"
                >
                  <a-input-number
                    v-model.number="unit.package.weight"
                    :min="1"
                    class="h-48px w-100 required"
                  />

                  <template #append>
                    <FormItem
                      tag="div"
                      rules="required"
                      :name="`Weight unit [${unit.unit}]`"
                      style="width: 80px; margin: 0 -11px; border-radius: 0 3px 3px 0"
                      class="bg-white ant-input-group-addon px-0"
                    >
                      <a-select 
                        v-model="unit.package.weight_unit"
                        :options="[
                          { value: 'GR', label: 'Gram' },
                          { value: 'KG', label: 'Kg' },
                        ]"
                        class="w-100 text-dark"
                      />
                    </FormItem>
                  </template>
                </FormInputGroup>
              </a-col>
            </a-row>

            <label class="mb-2">{{ $t('product.package_size') }}</label>
            <a-row type="flex" :gutter="16">
              <a-col :sm="{ span: 12 }" :md="{ span: 8 }" :lg="{ span: 8 }">
                <FormInputGroup
                  tag="div"
                  rules="required|numeric"
                  :label="$t('product.length')"
                  :name="`${$t('product.length')} [${unit.unit}]`"
                  label-class="floating-label"
                  class="h-48px mb-4"
                >
                  <a-input-number
                    v-model.number="unit.package.length"
                    :min="1"
                    class="h-48px w-100 required"
                  />

                  <template #append>
                    <FormItem
                      tag="div"
                      rules="required"
                      :name="`Satuan ${$t('product.length')} [${unit.unit}]`"
                      style="width: 80px; margin: 0 -11px; border-radius: 0 3px 3px 0"
                      class="bg-white ant-input-group-addon px-0"
                    >
                      <a-select 
                        v-model="unit.package.length_unit"
                        :options="PACKAGE_SIZES"
                        class="w-100 text-dark"
                        @change="val => $emit('changeUkuranPaket', val, index)"
                      />
                    </FormItem>
                  </template>
                </FormInputGroup>
              </a-col>
              <a-col :sm="{ span: 12 }" :md="{ span: 8 }" :lg="{ span: 8 }">
                <FormInputGroup
                  tag="div"
                  rules="required|numeric"
                  :label="$t('product.width')"
                  :name="`${$t('product.width')} [${unit.unit}]`"
                  label-class="floating-label"
                  class="h-48px mb-4"
                >
                  <a-input-number
                    v-model.number="unit.package.width"
                    :min="1"
                    class="h-48px w-100 required"
                  />

                  <template #append>
                    <FormItem
                      tag="div"
                      rules="required"
                      :name="`Satuan ${$t('product.width')} [${unit.unit}]`"
                      style="width: 80px; margin: 0 -11px; border-radius: 0 3px 3px 0"
                      class="bg-white ant-input-group-addon px-0"
                    >
                      <a-select 
                        v-model="unit.package.width_unit"
                        :options="PACKAGE_SIZES"
                        class="w-100 text-dark"
                        @change="val => $emit('changeUkuranPaket', val, index)"
                      />
                    </FormItem>
                  </template>
                </FormInputGroup>
              </a-col>
              <a-col :sm="{ span: 12 }" :md="{ span: 8 }" :lg="{ span: 8 }">
                <FormInputGroup
                  tag="div"
                  rules="required|numeric"
                  :label="$t('product.height')"
                  :name="`${$t('product.height')} [${unit.unit}]`"
                  label-class="floating-label"
                  class="h-48px mb-4"
                >
                  <a-input-number
                    v-model.number="unit.package.height"
                    :min="1"
                    class="h-48px w-100 required"
                  />

                  <template #append>
                    <FormItem
                      tag="div"
                      rules="required"
                      :name="`Satuan ${$t('product.height')} [${unit.unit}]`"
                      style="width: 80px; margin: 0 -11px; border-radius: 0 3px 3px 0"
                      class="bg-white ant-input-group-addon px-0"
                    >
                      <a-select 
                        v-model="unit.package.height_unit"
                        :options="PACKAGE_SIZES"
                        class="w-100 text-dark"
                        @change="val => $emit('changeUkuranPaket', val, index)"
                      />
                    </FormItem>
                  </template>
                </FormInputGroup>
              </a-col>
            </a-row>
          </div>

        <!-- <a-button ghost type="primary" icon="plus" @click="() => addUnit()">
          Tambah satuan baru
        </a-button> -->
        </div>
      </div>
      <a-row class="mb-4">
        <a-col :span="24" class="d-flex justify-content-between" style="gap: 10px;">
          <div class="desc">
            <label>
              {{ $t('product.cod') }}
            </label>
          </div>
          <div class="d-flex" style="margin-right: auto">
            <a-radio-group
              v-model="isItemCod"
              name="radioGroup"
              @change.prevent="$emit('changeCodProduct', $event.target.value)"
            >
              <a-radio :disabled="$route.query.edit && !edit" :value="0">
                {{ $t('utils.no') }}
              </a-radio>
              <a-radio :disabled="$route.query.edit && !edit" :value="1">
                {{ $t('utils.yes') }}
              </a-radio>
            </a-radio-group>
          </div>
        </a-col>
      </a-row>
    </ValidationObserver>
    <div v-if="$route.query.edit && edit" class="mt-3 py-3 text-right footer">
      <a-button
        size="large"
        type="primary"
        ghost
        class="px-5 mr-3 ml-auto"
        @click="handleEdit"
      >
        {{ $t('utils.cancel') }}
      </a-button>

      <a-button
        size="large"
        type="primary"
        class="px-5"
      >
        {{ $t('utils.save') }}
      </a-button>
    </div>
  </a-card>
</template>

<style lang="scss">
.floating-label {
  margin-bottom: -0.5rem !important;
  position: relative;
  display: table;
  z-index: 1;
  font-size: 80% !important;
  margin-left: .3rem;
  padding: 0 .35rem;
  background: #fff;
  color: #999;
  font-weight: normal !important;
}
.unit-item__body {
  .ant-input-group {
    &-addon {
      position: relative;

      .has-feedback.has-error {
        .ant-form-item-children-icon {
          display: none;
        }
        .ant-form-explain {
          font-size: 80%;
          position: absolute;
          right: 0;
          margin-top: .25rem;
        }
      }
    }
  }
}

.unit-selector--dropdown .ant-empty-description {
  white-space: normal;
  line-height: 1.2;
  font-size: 12px;
}
</style>

<style scoped lang="scss">
.unit-wrapper {
  > .unit-item:first-child {
    margin-bottom: 2rem;
  }

  > .unit-item:not(:first-child) {
    margin-bottom: 1rem;

    .unit-item__header {
      display: flex;
      background: #F2F4F8;
      border: 1px solid #dedede;
      padding: .5rem 1rem;
      border-radius: .25rem;
      align-items: center;

      &:hover {
        border-color: #495057;
      }
      
      .ant-btn {
        font-size: 90% !important;
      }

      .toggle {
        padding: 0 .25rem;
        color: var(--dark);
        border: 0;
        opacity: .5;
        margin-left: 1rem;
        transition: all .2s ease-in;
      }

      &--active {
        border-radius: .25rem .25rem 0 0;

        .toggle {
          transform: rotate(90deg);
        }
      }
    }

    .unit-item__body {
      border: 1px solid #dedede;
      padding: 1.5rem 2rem 0;
      border-radius: 0 0 .25rem .25rem;
    }
  }
}
.label_ekspedisi {
  float: left;
}
.label_free {
  background: #E2F5F3;
  color: #3FB6A7;
  border-radius: 5px;
  font-weight: bold;
  font-size: 13px;
}
.label_support {
  background: #FFF0EF;
  color: #F47F69;
  border-radius: 5px;
  font-weight: bold;
  border: 1px solid #FFCCCB;
  font-size: 13px;
}
</style>
